// Can the nudge email reach this owner? Asked of the server, which answers by
// the same steps a real cadence run takes (cadence_main.owner_reachability):
// a free-text owner such as "James and Nathan" looks assigned on the page but
// resolves to nobody, so its item is never nudged. Rows ask here; asks made in
// the same tick go out as one request, and answers are kept for the page load.
//
// A failed check is "couldn't check", never "unreachable": the flag only
// appears on a definite answer.

import { useEffect, useState } from 'react';
import { getUser } from '../auth/authService';

const ANALYTICS = `${process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? ''}`;

const answers = new Map(); // key -> {email, reachable, reason}
const pending = new Map(); // key -> {source, owner, owner_email}
const listeners = new Set();
let timer = null;

export const reachKey = (item) => `${item.source}|${String(item.owner || '').trim()}`;

async function flush() {
    timer = null;
    const batch = [...pending.values()];
    pending.clear();
    if (!batch.length) return;
    try {
        const user = await getUser();
        const res = await fetch(`${ANALYTICS}/api/work/owners/reachability`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.access_token}` },
            body: JSON.stringify({ owners: batch }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { owners = {} } = await res.json();
        Object.entries(owners).forEach(([k, v]) => answers.set(k, v));
    } catch {
        // No answer is recorded, so no flag is shown; a later page load asks again.
    }
    listeners.forEach((fn) => fn());
}

/** Queue an owner for checking (no-op when already answered or blank). */
export function askReachability(item) {
    const owner = String(item?.owner || '').trim();
    const key = reachKey(item || {});
    if (!owner || answers.has(key) || pending.has(key)) return;
    pending.set(key, { source: item.source, owner, owner_email: item.ownerEmail || '' });
    if (!timer) timer = setTimeout(flush, 0);
}

/** The answer for this item's owner, or null while unknown. Re-renders when it arrives. */
export function useOwnerReach(item) {
    const [, setTick] = useState(0);
    const key = reachKey(item || {});
    useEffect(() => {
        const fn = () => setTick((t) => t + 1);
        listeners.add(fn);
        askReachability(item);
        return () => listeners.delete(fn);
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
    return answers.get(key) || null;
}

/** Test hook. */
export function _resetOwnerReach() { answers.clear(); pending.clear(); }
